/**
 * Реестр интентов и событий онбординга (ТЗ §3.3, FR-24).
 *
 * Слой онбординга не вызывает доменные API: он лишь просит фичевый слой что-то
 * сделать (интент) и ждёт, что тот сообщит о результате (событие). Так тур не
 * дублирует бизнес-логику и не может изменить состояние в обход обычного пути.
 */

import { Sentry } from '../../lib/sentry';

export const ONBOARDING_INTENTS = ['mailbox.createModal.open'] as const;
export const ONBOARDING_EVENTS = ['mailbox.created', 'forwarding.confirmed'] as const;

export type OnboardingIntent = (typeof ONBOARDING_INTENTS)[number];
export type OnboardingEvent = (typeof ONBOARDING_EVENTS)[number];

type IntentHandler = () => void;
type EventListener = () => void;

const intentHandlers = new Map<OnboardingIntent, IntentHandler>();
const eventListeners = new Map<OnboardingEvent, Set<EventListener>>();

/** Регистрируется фичевым модулем при монтировании; возвращает функцию отписки. */
export function registerIntentHandler(intent: OnboardingIntent, handler: IntentHandler): () => void {
  intentHandlers.set(intent, handler);
  return () => {
    if (intentHandlers.get(intent) === handler) intentHandlers.delete(intent);
  };
}

/** Возвращает `false`, если обработчик не зарегистрирован — шаг тогда деградирует до информационного. */
export function dispatchIntent(intent: OnboardingIntent): boolean {
  const handler = intentHandlers.get(intent);
  if (!handler) {
    // В разработке это ошибка манифеста, в проде — повод для метрики деградаций (ТЗ §9.3),
    // но не причина ронять тур.
    if (import.meta.env.DEV) {
      console.error(`[onboarding] интент без обработчика: ${intent}`);
    }
    Sentry.captureMessage(`onboarding intent not registered: ${intent}`, 'warning');
    return false;
  }
  handler();
  return true;
}

export function subscribeOnboardingEvent(event: OnboardingEvent, listener: EventListener): () => void {
  const listeners = eventListeners.get(event) ?? new Set<EventListener>();
  listeners.add(listener);
  eventListeners.set(event, listeners);
  return () => {
    listeners.delete(listener);
  };
}

/** Вызывается фичевым модулем после реального изменения состояния. */
export function emitOnboardingEvent(event: OnboardingEvent): void {
  for (const listener of eventListeners.get(event) ?? []) listener();
}

/** Только для тестов: сбрасывает реестр между кейсами. */
export function resetOnboardingRegistry(): void {
  intentHandlers.clear();
  eventListeners.clear();
}
